/**
 * FR5 — Storyboard Workspace (mocks 08 + 11v2 + 12 + 13 + 14).
 *
 * Fullscreen view inside the batch page (`?batch=&item=&view=storyboard`):
 * the timeline's segments ARE the scene frames; the selected scene's details
 * live in a synced inspector; `&scene=<idx>` opens the per-scene fullscreen
 * (takes rail). The brief stage (`videoBrief`) fronts the flow — plan-first,
 * pay-on-render.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import type { AdCreativeItem, AdCreativeVideoPlan, AdCreativeVideoSegmentPlan } from '@segmently/schema-models/ad-creative';
import { ACTIVE_STATUSES, useTaskStore, useTaskTransition } from '@modules/background-tasks';
import { MediaLightbox, type MediaLightboxItem } from '@modules/ui-components/dialogs/MediaLightbox';
import { StyledTextField } from '@modules/ui-components';
import { AD_CREATIVE_STORYBOARD } from '../../selectors';
import type { AdCreativeApiClient } from '../../services/AdCreativeApiClient';
import { fetchAdCreativeItem } from '../../services/AdCreativeItemReader';
import {
  creativeLocaleHeadline,
  isSegmentlyOwnedCreativeItem,
} from '../../services/creativeLocaleProjection';
import { adCreativeTaskItemId, isAdCreativeVideoTask } from '../../services/adCreativeTaskScope';
import {
  AdCreativeInsightContextClient,
  INSIGHT_CATEGORY_COLOR,
  type AdCreativeInsightIndex,
} from '../../services/AdCreativeInsightContextClient';
import { useAdCreativeGenSettings } from '../../hooks/useAdCreativeGenSettings';
import { deriveSceneStaleness, deriveDraftCascade } from './storyboardStaleness';
import { StoryboardBriefStage } from './StoryboardBriefStage';
import { SceneFullscreen } from './SceneFullscreen';
import { InsertScenePopover, type InsertScenePayload } from './InsertScenePopover';

const SB = AD_CREATIVE_STORYBOARD;
const SAFETY_REFRESH_INTERVAL_MS = 60_000;
/** Veo estimate for per-scene cost captions (fast-preview rate). */
export const VIDEO_USD_PER_SECOND = 0.15;
const KEYFRAME_ESTIMATE_USD = 0.04;

export interface ThesisOption {
  sourcePath: string;
  category: string;
  label: string;
}

interface StoryboardWorkspaceProps {
  projectId: string;
  briefId: string;
  itemId: string;
  /** Fullscreen scene index from the URL (`&scene=`); null = timeline view. */
  sceneIndex: number | null;
  client: AdCreativeApiClient;
  onBack: () => void;
  onOpenScene: (index: number | null) => void;
}

interface SceneEditDraft {
  keyframePrompt: string;
  motionPrompt: string;
}

export function segmentThumbUrl(item: AdCreativeItem, segment: AdCreativeVideoSegmentPlan): string | undefined {
  const slot = (item.aiAssets ?? {})[segment.keyframeSlotKey] as
    | { selectedExperimentId?: string; experiments?: Record<string, { generatedUrl?: string; status?: string }> }
    | undefined;
  if (!slot?.experiments) return undefined;
  const id = slot.selectedExperimentId ?? Object.keys(slot.experiments)[0];
  const experiment = id ? slot.experiments[id] : undefined;
  return experiment?.status === 'completed' ? experiment.generatedUrl : undefined;
}

export function keyframeSlotOf(item: AdCreativeItem, segment: AdCreativeVideoSegmentPlan) {
  return (item.aiAssets ?? {})[segment.keyframeSlotKey] as
    | { selectedExperimentId?: string; experiments?: Record<string, { promptTemplate?: string; generatedUrl?: string; status?: string }> }
    | undefined;
}

/**
 * FR6 journey finding F3: a scene without an explicit sourcePath override
 * falls back to the BRIEF's role→thesis mapping for display (marked «auto») —
 * the grounding shown at brief time stays visible on the rendered plan.
 */
export function sceneThesisDisplay(
  scene: Pick<AdCreativeVideoSegmentPlan, 'sourcePath' | 'role'>,
  insightIndex: AdCreativeInsightIndex,
  brief?: AdCreativeItem['videoBrief'],
): { category?: string; label: string; auto: boolean } | undefined {
  if (scene.sourcePath) {
    const id = scene.sourcePath.match(/\[([^\]]+)\]$/)?.[1];
    const entry = id ? insightIndex[id] : undefined;
    if (entry) return { category: entry.category, label: entry.thesis, auto: false };
    return { label: scene.sourcePath, auto: false };
  }
  const mapping = (brief?.insightMapping ?? []).find((candidate) => candidate.role === scene.role);
  if (!mapping || (!mapping.label && !mapping.sourcePath)) return undefined;
  const category = mapping.sourcePath?.match(/\]\.([a-zA-Z]+)\[/)?.[1];
  return { category, label: mapping.label ?? mapping.sourcePath ?? '', auto: true };
}

function fullCutUrl(item: AdCreativeItem, plan?: AdCreativeVideoPlan): { videoUrl?: string; posterUrl?: string } {
  if (!plan) return {};
  const slot = (item.aiAssets ?? {})[plan.slotKey] as
    | { selectedExperimentId?: string; experiments?: Record<string, { generatedUrl?: string; posterUrl?: string; status?: string; assetType?: string }> }
    | undefined;
  if (!slot?.experiments) return {};
  const id = slot.selectedExperimentId ?? Object.keys(slot.experiments)[0];
  const experiment = id ? slot.experiments[id] : undefined;
  if (experiment?.status !== 'completed') return {};
  return { videoUrl: experiment.generatedUrl, posterUrl: experiment.posterUrl };
}

export function StoryboardWorkspace({
  projectId,
  briefId,
  itemId,
  sceneIndex,
  client,
  onBack,
  onOpenScene,
}: StoryboardWorkspaceProps): React.ReactElement {
  const [item, setItem] = useState<AdCreativeItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [stage, setStage] = useState<'brief' | 'scenes'>('scenes');
  const [selectedScene, setSelectedScene] = useState(0);
  const [playerMode, setPlayerMode] = useState<'full' | 'scene'>('full');
  const [lightbox, setLightbox] = useState<MediaLightboxItem[] | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<SceneEditDraft | null>(null);
  const [insertAt, setInsertAt] = useState<number | null>(null);
  const [thesisMenuAnchor, setThesisMenuAnchor] = useState<HTMLElement | null>(null);
  const [durationMenuAnchor, setDurationMenuAnchor] = useState<HTMLElement | null>(null);
  const [insightIndex, setInsightIndex] = useState<AdCreativeInsightIndex>({});
  const [selectionIds, setSelectionIds] = useState<string[] | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  // FR7: names of the batch brief's chosen creative frameworks.
  const [frameworkNames, setFrameworkNames] = useState<string[]>([]);
  // FR8-I2: 1-scene plans collapse to a single-scene brief card by default.
  const [forceTimeline, setForceTimeline] = useState(false);
  const genSettings = useAdCreativeGenSettings(projectId);

  const insightClient = useMemo(() => new AdCreativeInsightContextClient(projectId), [projectId]);

  // Returning from fullscreen keeps the last-viewed scene selected (AC-005).
  useEffect(() => {
    if (sceneIndex !== null) setSelectedScene(sceneIndex);
  }, [sceneIndex]);

  const refreshItem = useCallback(async () => {
    const fresh = await fetchAdCreativeItem(projectId, itemId);
    if (fresh && isSegmentlyOwnedCreativeItem(fresh)) setItem(fresh);
  }, [projectId, itemId]);
  const refreshItemRef = useRef(refreshItem);
  refreshItemRef.current = refreshItem;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchAdCreativeItem(projectId, itemId)
      .then((fresh) => {
        if (cancelled) return;
        if (isSegmentlyOwnedCreativeItem(fresh)) setItem(fresh);
        // Brief-first: without a plan the workspace opens on the brief stage.
        if (!fresh?.videoPlan?.segments?.length) setStage('brief');
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : String(loadError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, itemId]);

  // FR3 selection + labels: thesis menus offer ONLY the brief's selection.
  useEffect(() => {
    let cancelled = false;
    void client.getBrief(projectId, briefId).then((response) => {
      if (cancelled || !response.success || !response.data?.brief) return;
      const brief = response.data.brief as {
        insightsAnalysisId?: string;
        selectedInsightIds?: string[];
        insightSelectionMode?: string;
        networkId?: string;
        productContext?: { frameworkIds?: unknown; customCreativeBrief?: unknown };
      };
      const pinned = brief.insightsAnalysisId ?? null;
      setAnalysisId(pinned);
      setSelectionIds(brief.insightSelectionMode === 'manual' && Array.isArray(brief.selectedInsightIds)
        ? brief.selectedInsightIds
        : null);
      if (pinned) {
        void insightClient.loadInsightIndex(pinned).then((index) => {
          if (!cancelled) setInsightIndex(index);
        }).catch(() => undefined);
      }
      // FR7: framework picks persisted on the batch brief → shown on the
      // storyboard brief stage (names resolved from the served catalog).
      const frameworkIds = Array.isArray(brief.productContext?.frameworkIds)
        ? (brief.productContext!.frameworkIds as unknown[]).map(String).filter(Boolean)
        : [];
      if (frameworkIds.length > 0) {
        void client.getCatalog(brief.networkId ?? 'meta').then((catalog) => {
          if (cancelled) return;
          const byId = new Map((catalog.data?.frameworks ?? []).map((framework) => [framework.id, framework.name]));
          setFrameworkNames(frameworkIds.map((id) => byId.get(id) ?? id));
        }).catch(() => {
          if (!cancelled) setFrameworkNames(frameworkIds);
        });
      } else {
        setFrameworkNames([]);
      }
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [client, insightClient, projectId, briefId]);

  // Live statuses: any transition of this item's video task refreshes it (FR0).
  useTaskTransition({
    filter: (task) => isAdCreativeVideoTask(task) && adCreativeTaskItemId(task) === itemId,
    onTransition: () => {
      void refreshItemRef.current();
    },
  });
  const videoPlanStatus = item?.videoPlan?.status;
  useEffect(() => {
    if (videoPlanStatus !== 'running') return undefined;
    const interval = window.setInterval(() => {
      void refreshItemRef.current();
    }, SAFETY_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [videoPlanStatus]);

  const runningTaskId = useTaskStore((state) => {
    for (const task of Object.values(state.tasks)) {
      if (!isAdCreativeVideoTask(task)) continue;
      if (adCreativeTaskItemId(task) !== itemId) continue;
      if (ACTIVE_STATUSES.includes(task.status)) return task.id;
    }
    return null;
  });

  const plan = item?.videoPlan as AdCreativeVideoPlan | undefined;
  const segments = useMemo(
    () => [...(plan?.segments ?? [])].sort((a, b) => a.index - b.index),
    [plan?.segments],
  );
  const running = plan?.status === 'running' || Boolean(runningTaskId);
  const scene = segments.find((entry) => entry.index === selectedScene) ?? segments[0];
  const brief = item?.videoBrief;

  // Thesis options: manual selection ∩ index; auto → whole index.
  const thesisOptions: ThesisOption[] = useMemo(() => {
    if (!analysisId) return [];
    const entries = Object.entries(insightIndex)
      .filter(([id]) => (selectionIds ? selectionIds.includes(id) : true));
    return entries.map(([id, entry]) => ({
      sourcePath: `@insight[${analysisId}].${entry.category}[${id}]`,
      category: entry.category,
      label: entry.thesis,
    }));
  }, [insightIndex, selectionIds, analysisId]);

  const runOp = useCallback(async (
    label: string,
    op: Parameters<AdCreativeApiClient['videoPlanOps']>[2],
  ): Promise<boolean> => {
    setBusy(label);
    setError(null);
    try {
      const response = await client.videoPlanOps(projectId, itemId, op);
      if (!response.success) throw new Error(response.error || `Failed: ${label}`);
      await refreshItemRef.current();
      return true;
    } catch (opError) {
      setError(opError instanceof Error ? opError.message : String(opError));
      return false;
    } finally {
      setBusy(null);
    }
  }, [client, projectId, itemId]);

  const startGeneration = useCallback(async (
    label: string,
    body: Parameters<AdCreativeApiClient['generateItemVideo']>[2],
  ): Promise<boolean> => {
    setBusy(label);
    setError(null);
    try {
      const response = await client.generateItemVideo(projectId, itemId, {
        ...body,
        generation: genSettings.requestPayload,
      });
      if (!response.success) throw new Error(response.error || `Failed: ${label}`);
      await refreshItemRef.current();
      return true;
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : String(startError));
      return false;
    } finally {
      setBusy(null);
    }
  }, [client, projectId, itemId, genSettings.requestPayload]);

  const regenerateBrief = useCallback(async (instruction: string) => {
    const ok = await startGeneration('regenerate-brief', {
      regenerate: 'plan',
      targetDurationSec: brief?.targetDurationSec,
      // FIRST scene formation pre-renders the keyframe images too (cents, no
      // Veo) — pre-filled scenes arrive WITH their first frames. Re-plans of
      // an existing grid keep the explicit staleness/per-scene-regen flow.
      withKeyframes: segments.length === 0,
      userInstruction: instruction.trim() || undefined,
    });
    return ok;
  }, [startGeneration, brief?.targetDurationSec, segments.length]);

  const totalSec = segments.reduce((sum, entry) => sum + entry.durationSec, 0);
  const renderedSec = segments.filter((entry) => entry.clipUrl).reduce((sum, entry) => sum + entry.durationSec, 0);
  const fullCut = item ? fullCutUrl(item, plan) : {};

  if (loading) {
    return (
      <Box data-testid={SB.view} sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!item) {
    return (
      <Box data-testid={SB.view} sx={{ p: 3 }}>
        <Alert severity="error">Creative item not found.</Alert>
        <Button data-testid={SB.backToBatch} onClick={onBack} sx={{ mt: 1 }}>← Back to batch</Button>
      </Box>
    );
  }

  // Fullscreen per-scene mode (`&scene=<idx>`, mock 13).
  if (sceneIndex !== null && scene && segments.some((entry) => entry.index === sceneIndex)) {
    return (
      <SceneFullscreen
        item={item}
        plan={plan as AdCreativeVideoPlan}
        sceneIndex={sceneIndex}
        running={running}
        busy={busy}
        error={error}
        thesisOptions={thesisOptions}
        onBack={(currentIndex) => {
          setSelectedScene(currentIndex);
          onOpenScene(null);
        }}
        onNavigate={(index) => onOpenScene(index)}
        onUseTake={(index, takeId) => runOp('use-take', { op: 'useTake', segmentIndex: index, takeId })}
        onDeleteTake={(index, takeId) => runOp('delete-take', { op: 'deleteTake', segmentIndex: index, takeId })}
        onReroll={(index) => startGeneration('re-roll', { regenerate: 'segment', segmentIndex: index })}
        onRenderWithPatch={(index, patch) => startGeneration('render-take', {
          regenerate: 'segment',
          segmentIndex: index,
          segmentPatches: [{ index, ...patch }],
        })}
        onSetThesis={(index, sourcePath) => runOp('set-thesis', { op: 'setSceneSource', segmentIndex: index, sourcePath })}
        onRetime={(index, durationSec) => runOp('retime', { op: 'retime', segmentIndex: index, durationSec })}
        onOpenLightbox={(items) => setLightbox(items)}
      />
    );
  }

  const briefStageActive = stage === 'brief' || !segments.length;

  return (
    <Box data-testid={SB.view} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Top bar: back · title · stage rail */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" data-testid={SB.backToBatch} onClick={onBack}>← Back to batch</Button>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, minWidth: 200 }} noWrap>
          Video Storyboard — {creativeLocaleHeadline(item, item.defaultLanguage, 'strict')}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" data-testid={SB.stageRail}>
          <Chip
            size="small"
            data-testid={SB.stage('brief')}
            label={brief ? `✓ Brief v${brief.version}` : '1 · Brief'}
            color={briefStageActive ? 'primary' : 'default'}
            variant={brief ? 'filled' : 'outlined'}
            onClick={() => setStage('brief')}
          />
          <Typography variant="caption" color="text.secondary">──</Typography>
          <Chip
            size="small"
            data-testid={SB.stage('scenes')}
            label="2 · Scenes"
            color={!briefStageActive ? 'primary' : 'default'}
            variant="outlined"
            onClick={() => segments.length > 0 && setStage('scenes')}
            disabled={segments.length === 0}
          />
          <Typography variant="caption" color="text.secondary">──</Typography>
          <Chip
            size="small"
            data-testid={SB.stage('render')}
            label={`3 · Render${renderedSec > 0 ? ` · ${renderedSec}s` : ''}`}
            color={renderedSec > 0 ? 'success' : 'default'}
            variant="outlined"
          />
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {briefStageActive ? (
        <StoryboardBriefStage
          brief={brief}
          insightMapping={brief?.insightMapping}
          frameworkNames={frameworkNames}
          thesisOptions={thesisOptions}
          hasScenes={segments.length > 0}
          running={running}
          busy={busy}
          onSaveBrief={(patch) => runOp('update-brief', { op: 'updateBrief', ...patch })}
          onRegenerate={regenerateBrief}
          onApprove={() => setStage('scenes')}
        />
      ) : segments.length === 1 && !forceTimeline ? (
        /* FR8-I2 single-scene collapse: a 1-scene plan skips the timeline
           ceremony — the framework's structure collapsed into one scene brief;
           the full workspace stays one click away. */
        <Stack spacing={1.5} data-testid={SB.singleScene}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid={SB.briefBar}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.75 }}
          >
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              🧩 <b>Brief v{brief?.version ?? '—'}</b>{brief?.concept ? ` · ${brief.concept.slice(0, 110)}${brief.concept.length > 110 ? '…' : ''}` : ' · no brief yet'}
            </Typography>
            <Button size="small" data-testid={SB.briefBarExpand} onClick={() => setStage('brief')}>
              Expand brief ▾
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <Box sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}>
              {segments[0]?.clipUrl ? (
                <video key={segments[0].clipUrl} src={segments[0].clipUrl} controls style={{ width: '100%', borderRadius: 8 }} />
              ) : (
                <Box sx={{ aspectRatio: '9/16', borderRadius: 1, bgcolor: 'grey.900', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="caption" color="grey.400">not rendered yet</Typography>
                </Box>
              )}
            </Box>
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {`Single-scene brief · ${segments[0]?.role ?? 'scene'} · ${segments[0]?.durationSec ?? '—'}s`}
              </Typography>
              <Typography variant="caption" color="text.secondary">Keyframe prompt</Typography>
              <Typography variant="body2">{segments[0]?.keyframePrompt || '—'}</Typography>
              <Typography variant="caption" color="text.secondary">Motion prompt</Typography>
              <Typography variant="body2">{segments[0]?.motionPrompt || '—'}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="contained"
                  data-testid={SB.singleSceneRender}
                  disabled={running || busy !== null}
                  onClick={() => void startGeneration('re-render', { regenerate: 'segment', segmentIndex: segments[0].index })}
                >
                  {segments[0]?.clipUrl ? '↻ Re-render scene' : '▶ Render scene'}
                </Button>
                <Link
                  component="button"
                  variant="caption"
                  data-testid={SB.singleSceneTimeline}
                  onClick={() => setForceTimeline(true)}
                >
                  Открыть таймлайн →
                </Link>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <>
          {/* Collapsed brief line (mock 11) */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid={SB.briefBar}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.75 }}
          >
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
              🧩 <b>Brief v{brief?.version ?? '—'}</b>{brief?.concept ? ` · ${brief.concept.slice(0, 110)}${brief.concept.length > 110 ? '…' : ''}` : ' · no brief yet'}
            </Typography>
            <Button size="small" data-testid={SB.briefBarExpand} onClick={() => setStage('brief')}>
              Expand brief ▾
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            {/* Master player column */}
            <Box sx={{ width: { xs: '100%', md: 250 }, flexShrink: 0 }}>
              <Box
                data-testid={SB.player}
                sx={{
                  aspectRatio: '9/16',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'grey.900',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {playerMode === 'full' && fullCut.videoUrl ? (
                  <video
                    key={fullCut.videoUrl}
                    src={fullCut.videoUrl}
                    poster={fullCut.posterUrl}
                    controls
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : playerMode === 'scene' && scene?.clipUrl ? (
                  <video key={scene.clipUrl} src={scene.clipUrl} controls style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <Typography variant="caption" sx={{ color: 'grey.500', px: 2, textAlign: 'center' }}>
                    {playerMode === 'full' ? 'No full cut yet — render scenes below.' : 'Scene not rendered yet.'}
                  </Typography>
                )}
                {(playerMode === 'full' ? fullCut.videoUrl : scene?.clipUrl) && (
                  <IconButton
                    size="small"
                    data-testid={SB.playerLightbox}
                    onClick={() => {
                      const url = playerMode === 'full' ? fullCut.videoUrl : scene?.clipUrl;
                      if (!url) return;
                      setLightbox([{ id: 'master', kind: 'video', src: url, title: playerMode === 'full' ? 'Full cut' : `Scene ${(scene?.index ?? 0) + 1}` }]);
                    }}
                    sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,.55)', color: '#fff' }}
                  >
                    🔍
                  </IconButton>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  data-testid={SB.playerMode('full')}
                  label="Full cut"
                  color={playerMode === 'full' ? 'primary' : 'default'}
                  variant={playerMode === 'full' ? 'filled' : 'outlined'}
                  onClick={() => setPlayerMode('full')}
                />
                <Chip
                  size="small"
                  data-testid={SB.playerMode('scene')}
                  label={`Scene ${(scene?.index ?? 0) + 1}`}
                  color={playerMode === 'scene' ? 'primary' : 'default'}
                  variant={playerMode === 'scene' ? 'filled' : 'outlined'}
                  onClick={() => setPlayerMode('scene')}
                />
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" color={renderedSec > 0 ? 'success' : 'default'} variant="outlined" label={`${segments.filter((entry) => entry.clipUrl).length}/${segments.length} scenes · ${renderedSec}s rendered`} />
                {running && <Chip size="small" color="info" label="rendering…" />}
              </Stack>
            </Box>

            {/* Timeline + inspector */}
            <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Timeline · click = select scene · thumbnails are the scene first frames
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    total {totalSec}s · {renderedSec}s rendered
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="stretch" data-testid={SB.timeline} sx={{ minHeight: 150 }}>
                  {segments.map((segment, position) => {
                    const staleness = deriveSceneStaleness(segment, keyframeSlotOf(item, segment));
                    const thumb = segmentThumbUrl(item, segment);
                    const isSelected = segment.index === (scene?.index ?? -1);
                    return (
                      <React.Fragment key={segment.index}>
                        <Tooltip title="Insert scene here">
                          <Box
                            component="button"
                            data-testid={SB.insertHotspot(position)}
                            onClick={() => setInsertAt(position)}
                            disabled={running || segments.length >= 8}
                            sx={{
                              alignSelf: 'center', width: 20, height: 20, borderRadius: '50%',
                              border: '1px dashed', borderColor: 'divider', bgcolor: 'background.paper',
                              color: 'text.secondary', cursor: 'pointer', fontSize: 12, p: 0,
                              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                            }}
                          >
                            +
                          </Box>
                        </Tooltip>
                        <Box
                          data-testid={SB.timelineSegment(segment.index)}
                          onClick={() => {
                            setSelectedScene(segment.index);
                            setPlayerMode('scene');
                            setEditMode(false);
                            setDraft(null);
                          }}
                          sx={{
                            flex: segment.durationSec,
                            minWidth: 64,
                            borderRadius: 1,
                            border: 2,
                            borderColor: isSelected ? 'primary.main' : segment.status === 'error' ? 'error.main' : 'divider',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: isSelected ? '0 0 0 2px rgba(25,118,210,.3)' : undefined,
                          }}
                        >
                          <Box sx={{ flex: 1, position: 'relative', bgcolor: 'grey.800', minHeight: 96 }}>
                            {thumb && (
                              <Box component="img" src={thumb} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
                            )}
                            <Chip
                              size="small"
                              label={`${segment.index + 1} · ${(segment.role ?? 'scene').toUpperCase()}`}
                              sx={{ position: 'absolute', top: 4, left: 4, bgcolor: 'rgba(0,0,0,.55)', color: '#fff', height: 18, fontSize: 10 }}
                            />
                            <Chip
                              size="small"
                              data-testid={SB.segmentStatus(segment.index)}
                              label={
                                segment.status === 'completed'
                                  ? (staleness.clipOutdated ? 'outdated' : '✓')
                                  : segment.status === 'generating'
                                    ? '…'
                                    : segment.status === 'error'
                                      ? 'retry'
                                      : segment.status === 'keyframe_ready' ? 'ready' : 'draft'
                              }
                              color={
                                segment.status === 'completed'
                                  ? (staleness.clipOutdated ? 'warning' : 'success')
                                  : segment.status === 'generating'
                                    ? 'info'
                                    : segment.status === 'error' ? 'error' : 'default'
                              }
                              sx={{ position: 'absolute', top: 4, right: 4, height: 18, fontSize: 10 }}
                            />
                            {segment.needsReanchor && (
                              <Chip size="small" color="warning" label="re-anchor" sx={{ position: 'absolute', bottom: 4, left: 4, height: 18, fontSize: 10 }} />
                            )}
                          </Box>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.75, py: 0.25, borderTop: 1, borderColor: 'divider' }}>
                            {segment.clipUrl && <Typography variant="caption">▶</Typography>}
                            {segment.sourcePath && insightIndex[segment.sourcePath.match(/\[([^\]]+)\]$/)?.[1] ?? ''] && (
                              <Chip
                                size="small"
                                color={INSIGHT_CATEGORY_COLOR[insightIndex[segment.sourcePath.match(/\[([^\]]+)\]$/)?.[1] ?? '']?.category ?? ''] ?? 'default'}
                                label={insightIndex[segment.sourcePath.match(/\[([^\]]+)\]$/)?.[1] ?? '']?.category}
                                sx={{ height: 16, fontSize: 9 }}
                              />
                            )}
                            <Box
                              component="button"
                              data-testid={SB.segmentRetime(segment.index)}
                              onClick={(event: React.MouseEvent<HTMLElement>) => {
                                event.stopPropagation();
                                setSelectedScene(segment.index);
                                setDurationMenuAnchor(event.currentTarget);
                              }}
                              disabled={running}
                              sx={{ marginLeft: 'auto', border: 0, background: 'none', cursor: 'pointer', color: 'text.secondary', fontSize: 11, p: 0 }}
                            >
                              {segment.durationSec}s ▾
                            </Box>
                          </Stack>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                  <Box
                    component="button"
                    data-testid={SB.addScene}
                    onClick={() => setInsertAt(segments.length)}
                    disabled={running || segments.length >= 8}
                    sx={{
                      flex: 2, minWidth: 90, border: '2px dashed', borderColor: 'divider', borderRadius: 1,
                      color: 'text.secondary', bgcolor: 'transparent', cursor: 'pointer', fontSize: 12,
                    }}
                  >
                    + Add scene ({segments.length}/8)
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="space-between" data-testid={SB.ruler} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">0s</Typography>
                  <Typography variant="caption" color="text.secondary">{totalSec}s</Typography>
                </Stack>
              </Box>

              {/* Inspector — selected scene */}
              {scene && (
                <SceneInspector
                  item={item}
                  scene={scene}
                  running={running}
                  busy={busy}
                  editMode={editMode}
                  draft={draft}
                  insightIndex={insightIndex}
                  onEnterEdit={() => {
                    setEditMode(true);
                    setDraft({ keyframePrompt: scene.keyframePrompt, motionPrompt: scene.motionPrompt });
                  }}
                  onDraftChange={(next) => setDraft(next)}
                  onDiscard={() => {
                    setEditMode(false);
                    setDraft(null);
                  }}
                  onApplyFull={async () => {
                    if (!draft) return;
                    const ok = await startGeneration('apply-full', {
                      regenerate: 'segment',
                      segmentIndex: scene.index,
                      segmentPatches: [{ index: scene.index, keyframePrompt: draft.keyframePrompt, motionPrompt: draft.motionPrompt }],
                    });
                    if (ok) {
                      setEditMode(false);
                      setDraft(null);
                    }
                  }}
                  onApplyClipOnly={async () => {
                    if (!draft) return;
                    const ok = await startGeneration('apply-clip', {
                      regenerate: 'segment',
                      segmentIndex: scene.index,
                      segmentPatches: [{ index: scene.index, motionPrompt: draft.motionPrompt }],
                    });
                    if (ok) {
                      setEditMode(false);
                      setDraft(null);
                    }
                  }}
                  onRegenerateKeyframe={() => runOp('regenerate-keyframe', { op: 'regenerateKeyframe', segmentIndex: scene.index })}
                  onPickKeyframe={(experimentId) => runOp('set-keyframe', { op: 'setKeyframe', segmentIndex: scene.index, source: 'experiment', experimentId })}
                  onRerender={() => startGeneration('re-render', { regenerate: 'segment', segmentIndex: scene.index })}
                  onOpenFullscreen={() => onOpenScene(scene.index)}
                  onOpenFrame={() => {
                    const thumb = segmentThumbUrl(item, scene);
                    if (thumb) setLightbox([{ id: `kf-${scene.index}`, kind: 'image', src: thumb, title: `Scene ${scene.index + 1} keyframe` }]);
                  }}
                  onOpenThesisMenu={(anchor) => setThesisMenuAnchor(anchor)}
                />
              )}

              {/* Footer actions */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Completed clips are reused — only changed scenes re-render (paid).
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  data-testid={SB.allKeyframes}
                  disabled={running || busy !== null}
                  onClick={() => void startGeneration('all-keyframes', { regenerate: 'keyframes', targetDurationSec: plan?.targetDurationSec })}
                >
                  ↻ All keyframes
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  data-testid={SB.renderRemaining}
                  disabled={running || busy !== null}
                  onClick={() => void startGeneration('render-remaining', {
                    // A reviewed plan exists on this stage — 'videos' keeps the
                    // draft prompts + pre-rendered keyframes and renders clips;
                    // 'all' would re-run the storyboard agent and DISCARD them.
                    regenerate: segments.length > 0 ? 'videos' : 'all',
                    targetDurationSec: plan?.targetDurationSec,
                  })}
                >
                  {`${renderedSec > 0 ? 'Render remaining' : '▶ Render all scenes'} → ≈$${((totalSec - renderedSec) * VIDEO_USD_PER_SECOND).toFixed(2)} · estimated`}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </>
      )}

      {/* Thesis change menu (FR3 selection only) */}
      <Menu open={Boolean(thesisMenuAnchor)} anchorEl={thesisMenuAnchor} onClose={() => setThesisMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setThesisMenuAnchor(null);
            if (scene) void runOp('set-thesis', { op: 'setSceneSource', segmentIndex: scene.index, sourcePath: null });
          }}
        >
          <ListItemText primary="No source (brand-generic)" />
        </MenuItem>
        {thesisOptions.map((option) => (
          <MenuItem
            key={option.sourcePath}
            onClick={() => {
              setThesisMenuAnchor(null);
              if (scene) void runOp('set-thesis', { op: 'setSceneSource', segmentIndex: scene.index, sourcePath: option.sourcePath });
            }}
          >
            <Chip size="small" color={INSIGHT_CATEGORY_COLOR[option.category] ?? 'default'} label={option.category} sx={{ mr: 1 }} />
            <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={option.label.slice(0, 80)} />
          </MenuItem>
        ))}
      </Menu>

      {/* Retime menu (4|6|8) */}
      <Menu open={Boolean(durationMenuAnchor)} anchorEl={durationMenuAnchor} onClose={() => setDurationMenuAnchor(null)}>
        {[4, 6, 8].map((seconds) => (
          <MenuItem
            key={seconds}
            onClick={() => {
              setDurationMenuAnchor(null);
              if (scene) void runOp('retime', { op: 'retime', segmentIndex: scene.index, durationSec: seconds });
            }}
          >
            {seconds}s
          </MenuItem>
        ))}
      </Menu>

      {/* Insert popover (mock 14) */}
      {insertAt !== null && plan && (
        <InsertScenePopover
          plan={plan}
          position={insertAt}
          thesisOptions={thesisOptions}
          busy={busy === 'insert-scene'}
          onClose={() => setInsertAt(null)}
          onInsert={async (payload: InsertScenePayload) => {
            const ok = await runOp('insert-scene', {
              op: 'insertScene',
              position: payload.position,
              role: payload.role,
              durationSec: payload.durationSec,
              sourcePath: payload.sourcePath,
            });
            if (ok) {
              setInsertAt(null);
              setSelectedScene(payload.position);
            }
          }}
        />
      )}

      {lightbox && (
        <MediaLightbox open items={lightbox} index={0} onIndexChange={() => undefined} onClose={() => setLightbox(null)} />
      )}
    </Box>
  );
}

// ── Inspector (mocks 11 read-mode + 12 edit-mode) ──────────────────

interface SceneInspectorProps {
  item: AdCreativeItem;
  scene: AdCreativeVideoSegmentPlan;
  running: boolean;
  busy: string | null;
  editMode: boolean;
  draft: SceneEditDraft | null;
  insightIndex: AdCreativeInsightIndex;
  onEnterEdit: () => void;
  onDraftChange: (draft: SceneEditDraft) => void;
  onDiscard: () => void;
  onApplyFull: () => void;
  onApplyClipOnly: () => void;
  onRegenerateKeyframe: () => void;
  onPickKeyframe: (experimentId: string) => void;
  onRerender: () => void;
  onOpenFullscreen: () => void;
  onOpenFrame: () => void;
  onOpenThesisMenu: (anchor: HTMLElement) => void;
}

function SceneInspector({
  item,
  scene,
  running,
  busy,
  editMode,
  draft,
  insightIndex,
  onEnterEdit,
  onDraftChange,
  onDiscard,
  onApplyFull,
  onApplyClipOnly,
  onRegenerateKeyframe,
  onPickKeyframe,
  onRerender,
  onOpenFullscreen,
  onOpenFrame,
  onOpenThesisMenu,
}: SceneInspectorProps): React.ReactElement {
  const slot = keyframeSlotOf(item, scene);
  const staleness = deriveSceneStaleness(scene, slot);
  const thumb = segmentThumbUrl(item, scene);
  const isChain = scene.continuityMode === 'chain';
  const takesCount = scene.takes?.length ?? 0;
  const draftCascade = draft
    ? deriveDraftCascade({
      keyframePromptDirty: draft.keyframePrompt !== scene.keyframePrompt,
      motionPromptDirty: draft.motionPrompt !== scene.motionPrompt,
    })
    : null;
  const thesis = sceneThesisDisplay(scene, insightIndex, item.videoBrief);
  // Stable strip order: Firestore map iteration is not insertion-ordered.
  const experimentEntries = Object.entries(slot?.experiments ?? {})
    .sort(([left], [right]) => left.localeCompare(right));
  const sceneCost = (scene.durationSec * VIDEO_USD_PER_SECOND).toFixed(2);

  return (
    <Box
      data-testid={SB.inspector}
      sx={{ border: 1, borderColor: 'divider', borderLeft: 3, borderLeftColor: 'primary.main', borderRadius: 1, p: 1.5 }}
    >
      {(staleness.clipOutdated || staleness.keyframeOutdated || (draftCascade && (draftCascade.clipOutdated || draftCascade.keyframeOutdated))) && (
        <Alert severity="warning" data-testid={SB.staleBanner} sx={{ mb: 1 }}>
          {draftCascade && (draftCascade.keyframeOutdated || draftCascade.clipOutdated)
            ? `Edited prompts: ${draftCascade.keyframeOutdated ? 'keyframe outdated → ' : ''}clip outdated after apply.`
            : staleness.reasons.join(' · ')}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {/* Keyframe column */}
        <Box sx={{ width: 120, flexShrink: 0 }}>
          <Box
            onClick={onOpenFrame}
            sx={{ aspectRatio: '9/16', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.800', position: 'relative', cursor: thumb ? 'pointer' : 'default' }}
          >
            {thumb && <Box component="img" src={thumb} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
            {staleness.keyframeOutdated && (
              <Chip size="small" color="warning" label="outdated" sx={{ position: 'absolute', top: 4, right: 4, height: 18, fontSize: 10 }} />
            )}
            {scene.clipUrl && (
              <Chip size="small" label={`clip ${scene.durationSec}s`} sx={{ position: 'absolute', bottom: 4, left: 4, height: 18, fontSize: 10, bgcolor: 'rgba(0,0,0,.55)', color: '#fff' }} />
            )}
          </Box>
          {editMode && !isChain && (
            <>
              <Stack direction="row" spacing={0.5} data-testid={SB.kfVersions} sx={{ mt: 0.5, overflowX: 'auto' }}>
                {experimentEntries.map(([experimentId, experiment], index) => (
                  <Box
                    key={experimentId}
                    component="button"
                    data-testid={SB.kfVersion(index)}
                    onClick={() => onPickKeyframe(experimentId)}
                    title={`v${index + 1}`}
                    sx={{
                      width: 28, height: 44, borderRadius: 0.5, p: 0, overflow: 'hidden', cursor: 'pointer',
                      border: 2,
                      borderColor: slot?.selectedExperimentId === experimentId ? 'primary.main' : 'divider',
                      bgcolor: 'grey.800',
                    }}
                  >
                    {experiment.generatedUrl && <Box component="img" src={experiment.generatedUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </Box>
                ))}
              </Stack>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Button size="small" variant="outlined" data-testid={SB.kfAction('regenerate')} disabled={running || busy !== null} onClick={onRegenerateKeyframe}>
                  ↻ Keyframe · ≈${KEYFRAME_ESTIMATE_USD} · estimated
                </Button>
              </Stack>
            </>
          )}
        </Box>

        {/* Details column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {scene.index + 1} · {(scene.role ?? 'scene').toUpperCase()}
            </Typography>
            <Chip
              size="small"
              label={editMode ? 'edited — not rendered' : scene.status}
              color={editMode ? 'warning' : scene.status === 'completed' ? 'success' : scene.status === 'error' ? 'error' : 'default'}
            />
            <Typography variant="caption" color="text.secondary">
              continuity: {isChain ? 'chain (inherited frame)' : 'own keyframe'}
            </Typography>
          </Stack>

          {isChain && editMode && (
            <Alert severity="info" data-testid={SB.chainCard} sx={{ mb: 1 }}>
              Chain scene — its first frame is the previous scene's last frame, extracted automatically. Keyframe editing is disabled.
            </Alert>
          )}

          {editMode && draft ? (
            <Stack spacing={1}>
              <Box data-testid={SB.inspectorEdit('keyframe')}>
                <Typography variant="caption" color="text.secondary">
                  Keyframe prompt {draft.keyframePrompt !== scene.keyframePrompt && <Chip size="small" color="warning" label="edited" sx={{ height: 16, fontSize: 9, ml: 0.5 }} />}
                </Typography>
                <StyledTextField
                  value={draft.keyframePrompt}
                  disabled={isChain}
                  onChange={(value) => onDraftChange({ ...draft, keyframePrompt: value })}
                  multiline
                  minRows={3}
                  size="small"
                  fullWidth
                />
              </Box>
              <Box data-testid={SB.inspectorEdit('motion')}>
                <Typography variant="caption" color="text.secondary">
                  Motion prompt {draft.motionPrompt !== scene.motionPrompt && <Chip size="small" color="warning" label="edited" sx={{ height: 16, fontSize: 9, ml: 0.5 }} />}
                </Typography>
                <StyledTextField
                  value={draft.motionPrompt}
                  onChange={(value) => onDraftChange({ ...draft, motionPrompt: value })}
                  multiline
                  minRows={3}
                  size="small"
                  fullWidth
                />
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="contained" data-testid={SB.applyAction('full')} disabled={running || busy !== null} onClick={onApplyFull}>
                  Apply → keyframe → re-render scene · ≈${sceneCost} · estimated
                </Button>
                <Button size="small" variant="outlined" data-testid={SB.applyAction('clipOnly')} disabled={running || busy !== null} onClick={onApplyClipOnly}>
                  Re-render clip only · ≈${sceneCost} · estimated
                </Button>
                <Button size="small" data-testid={SB.applyAction('discard')} onClick={onDiscard}>Discard edits</Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Only this scene re-renders — other scenes keep their clips and re-stitch for free.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={0.75}>
              <Box data-testid={SB.inspectorPrompt('keyframe')}>
                <Stack direction="row" alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em' }}>Keyframe prompt</Typography>
                  <Button size="small" data-testid={SB.inspectorEdit('enter')} onClick={onEnterEdit} disabled={running} sx={{ ml: 'auto', minWidth: 0 }}>✎ edit</Button>
                </Stack>
                <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {scene.keyframePrompt}
                </Typography>
              </Box>
              <Box data-testid={SB.inspectorPrompt('motion')}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em' }}>Motion prompt</Typography>
                <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {scene.motionPrompt}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  data-testid={SB.inspectorThesis}
                  variant="outlined"
                  color={thesis?.category ? INSIGHT_CATEGORY_COLOR[thesis.category] ?? 'default' : 'default'}
                  label={thesis
                    ? `${thesis.category ? `${thesis.category} · ` : ''}${thesis.label.slice(0, 48)}${thesis.label.length > 48 ? '…' : ''}${thesis.auto ? ' · auto' : ''}`
                    : (scene.role === 'cta' ? 'no source needed' : 'no thesis')}
                />
                <Button
                  size="small"
                  data-testid={SB.inspectorThesisChange}
                  disabled={running}
                  onClick={(event) => onOpenThesisMenu(event.currentTarget)}
                >
                  change ▾
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" data-testid={SB.inspectorAction('fullscreen')} onClick={onOpenFullscreen}>
                  ⤢ Open scene full screen
                </Button>
                <Button size="small" data-testid={SB.inspectorAction('frame')} onClick={onOpenFrame} disabled={!thumb}>🔍 Frame</Button>
                {!isChain && (
                  <Button size="small" data-testid={SB.inspectorAction('keyframe')} disabled={running || busy !== null} onClick={onRegenerateKeyframe}>
                    ↻ Keyframe · ≈${KEYFRAME_ESTIMATE_USD} · estimated
                  </Button>
                )}
                {/* Never-rendered scene: the render CTA is the PRIMARY action
                    («Render scene»), not a text-styled «Re-render». */}
                <Button
                  size="small"
                  variant={scene.clipUrl ? 'text' : 'contained'}
                  data-testid={SB.inspectorAction('rerender')}
                  disabled={running || busy !== null}
                  onClick={onRerender}
                >
                  {scene.clipUrl
                    ? `↻ Re-render · ≈$${sceneCost} · estimated`
                    : `▶ Render scene · ≈$${sceneCost} · estimated`}
                </Button>
                <Typography variant="caption" color="text.secondary" data-testid={SB.inspectorTakes} sx={{ ml: 'auto' }}>
                  Takes: {takesCount}{scene.activeTakeId && takesCount > 0 ? ` · using #${(scene.takes ?? []).findIndex((take) => take.id === scene.activeTakeId) + 1}` : ''} · {scene.durationSec}s
                </Typography>
              </Stack>
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
